import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * One photograph of a partner venue, as its detail payload carries it.
 *
 * **It is an id and a position — deliberately not a url.** `MediaUploadPort`
 * ships no `mintReadUrl`, because "who may read this asset" is a property of the
 * asset's *kind* and one authorization path is what stops two surfaces drifting
 * apart on the same question. So every reader — the staff portal's grid and, from
 * P25e, a member browsing the directory — exchanges this id at
 * `GET /media/:id/url`, which is also the route that applies the `venue_photo`
 * kind's `memberRead: 'open'` rule. Inlining a url here would additionally bake a
 * short-lived signature into a payload that is cached by id.
 *
 * It is likewise not `width`/`height`/`blurHash`: those live on `media_assets`,
 * which the `meetings` module may not read (U1). The mint answers with them.
 */
export const VenuePhotoSchema = z
  .object({
    mediaAssetId: UuidSchema,
    /** Ascending, from the venue's own ordering. Dense from zero after any write. */
    sortOrder: z.number().int().nonnegative(),
  })
  .strict();

export type VenuePhoto = z.infer<typeof VenuePhotoSchema>;

/**
 * Asking to upload one photograph against a venue.
 *
 * `OrderEvidencePresignSchema` **minus the kind**, and that omission is the point
 * rather than a convenience: this route always presigns `venue_photo`, and a
 * client able to name the kind would be a client able to mint an upload slot for
 * an `id_document` through a `meetings.manage` permission. The route knows what
 * it is for; the caller only declares what it intends to send — and a caller that
 * lies about type or size is refused by storage itself, because the signature
 * covers both headers.
 */
export const VenuePhotoPresignSchema = z
  .object({
    mime: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
  })
  .strict();

export type VenuePhotoPresignRequest = z.infer<typeof VenuePhotoPresignSchema>;

/**
 * Finalizing the upload and binding it to the venue, in one call.
 *
 * Deliberately **not** two, for the reason billing's receipt records: a finalize
 * followed by a separate attach leaves a window in which a `ready` photograph
 * belongs to nothing, and a client that dies in that window leaves the purge
 * worker to guess. The server finalizes, re-reads the asset's kind, and claims a
 * place under the venue's cap in one act.
 *
 * **No `sortOrder`.** An attach that could also place the photograph would be two
 * decisions in one body, and the placing half would race the grid the operator is
 * looking at — a new photograph goes last, and moving it is
 * {@link VenuePhotoOrderSchema}'s job.
 */
export const VenuePhotoAttachSchema = z
  .object({
    mediaAssetId: UuidSchema,
  })
  .strict();

export type VenuePhotoAttachRequest = z.infer<typeof VenuePhotoAttachSchema>;

/**
 * Re-ordering a venue's photographs: **the whole order, as one list**.
 *
 * Never "move this one to position 3". A positional write is evaluated against
 * whatever the server holds *now*, so two operators — or one operator with a
 * stale tab — silently produce an arrangement neither asked for. Sending the
 * complete order makes the request self-describing, and lets the server refuse a
 * list that does not name exactly the photographs the venue currently has
 * (`venuePhotoOrderMismatch`) instead of quietly dropping the ones it omits.
 *
 * The duplicate check is here rather than only in the service because a repeated
 * id is malformed on its face: left to the set comparison it would surface as
 * "you are missing a photograph", which is a confusing lie about what is wrong.
 */
export const VenuePhotoOrderSchema = z
  .object({
    mediaAssetIds: z.array(UuidSchema).refine((ids) => new Set(ids).size === ids.length, {
      message: 'A photograph may appear in the order only once.',
    }),
  })
  .strict();

export type VenuePhotoOrderRequest = z.infer<typeof VenuePhotoOrderSchema>;
